import { estimateRuntime, estimateTimeline, formatRange, isSpeakable, type Script } from '@reelmachine/core';
import { Icon } from '../../ui/Icon';
import type { ScriptEditor } from './useScriptEditor';

export const lineName = (i: number) => (i === 0 ? 'Hook' : `Line ${i + 1}`);

interface Props {
  script: Script;
  editor: ScriptEditor;
  highlight: ReadonlySet<string>;
  readOnly: boolean;
}

export function ScriptTab({ script, editor, highlight, readOnly }: Props) {
  // Timing follows the text as typed.
  const lines = script.lines.map((l) => ({ ...l, text: editor.value(l.id, 'text') }));
  const timeline = estimateTimeline({ lines });
  const cls = (k: string) => (highlight.has(k) ? 'changed' : undefined);
  return (
    <>
      <div className={`script-head ${cls('title') ?? ''}`.trim()}>
        <h2 className="script-title">{script.title}</h2>
        <p className="nums">About {Math.round(estimateRuntime({ lines }))} s</p>
      </div>
      {script.lines.map((line, i) => (
        <section className={`card ${cls(`line:${line.id}`) ?? ''}`.trim()} key={line.id} aria-label={lineName(i)}>
          <div className="row between">
            <span className="label" style={{ color: 'var(--ink)' }}>
              {lineName(i)}
            </span>
            <span className="row" style={{ gap: 8 }}>
              <span className="nums">{timeline[i] && formatRange(timeline[i])}</span>
              {!readOnly && script.lines.length > 1 && (
                <button
                  type="button"
                  className="iconbtn sm"
                  aria-label={`Remove ${lineName(i)}`}
                  title="Remove line"
                  onClick={() => void editor.remove(line.id)}
                >
                  <Icon name="x" />
                </button>
              )}
            </span>
          </div>
          <div className="field">
            <label className="field-l" htmlFor={`text-${line.id}`}>
              Spoken
            </label>
            <textarea
              id={`text-${line.id}`}
              rows={3}
              maxLength={600}
              readOnly={readOnly}
              className={cls(`text:${line.id}`)}
              value={editor.value(line.id, 'text')}
              onChange={(e) => editor.set(line.id, 'text', e.target.value)}
              aria-invalid={!isSpeakable(editor.value(line.id, 'text'))}
              aria-describedby={isSpeakable(editor.value(line.id, 'text')) ? undefined : `empty-${line.id}`}
            />
            {!isSpeakable(editor.value(line.id, 'text')) && (
              <p className="error-text small" id={`empty-${line.id}`}>
                No spoken text. Write the line or remove it, otherwise the video cannot be made.
              </p>
            )}
          </div>
          <div className="field">
            <label className="field-l" htmlFor={`onScreen-${line.id}`}>
              On screen
            </label>
            <input
              type="text"
              id={`onScreen-${line.id}`}
              maxLength={200}
              readOnly={readOnly}
              className={cls(`onScreen:${line.id}`)}
              value={editor.value(line.id, 'onScreen')}
              onChange={(e) => editor.set(line.id, 'onScreen', e.target.value)}
            />
          </div>
        </section>
      ))}
    </>
  );
}
